import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Select,
  SelectItem,
} from '@heroui/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Descendant, Node } from 'slate';
import styled from 'styled-components';
import { useAuth } from '../../../hooks/useAuth';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { Workspace } from '../../../services/api/workspaceController';
import { createInitialValue } from '../../../utils/richText';
import RichTextEditor from '../../../utils/RichTextEditor';
import { useCreateWorkspace } from '../../../hooks/workspace/useCreateWorkspace';
import { isCompanyWorkspace } from '../../../utils/companyWorkspace';
import { employmentTypeOptions } from '../Shared/employmentTypeOptions';
import { currencyOptions } from '../Shared/currencyOptions';
import {
  DEFAULT_SALARY_PERIOD,
  salaryPeriodOptions,
} from '../Shared/salaryPeriodOptions';
import breakpointsMinWidth from '../../../styles/breakpointsMinWidth';

const CREATE_COMPANY_OPTION_KEY = '__create_company_option__';
const NO_MATCHING_COMPANIES_OPTION_KEY = '__no_matching_companies_option__';

const AutocompleteInputWrapper = styled(Autocomplete)`
  [data-slot='input-wrapper'] {
    border-radius: 6px;
    padding: 0 12px;
    min-height: 0 !important;
    height: 42px;
    font-size: 16px;
    color: #1f2937;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db;
    box-shadow: none;
    &:hover {
      background-color: #ffffff !important;
    }
    &:hover-visible:ring-0 {
      outline: none;
      box-shadow: none;
      background-color: #ffffff !important;
    }
    &:hover-visible {
      outline: none;
      box-shadow: none;
      background-color: #ffffff !important;
    }
    &:focus {
      outline: none;
      box-shadow: none;
      background-color: #ffffff;
      border: 1px solid #8510df !important;
    }
    &:focus-visible {
      outline: none;
      box-shadow: none;
      background-color: #ffffff;
      border: 1px solid #8510df !important;
    }
    &:group-data-[focus-visible='true']:ring-0 {
      outline: none;
      box-shadow: none;
      background-color: #ffffff;
      border-color: #8510df !important;
    }
    @media ${breakpointsMinWidth.tabletMd} {
      height: 36px !important;
    }
    @media ${breakpointsMinWidth.desktopMd} {
      height: 42px !important;
    }
  }
  [data-slot='input-wrapper'][data-focus='true'] {
    border: 1px solid #8510df !important;
  }

  [data-slot='input-wrapper'][data-focus-visible='true'],
  [data-slot='input-wrapper']:focus-visible {
    outline: none;
    box-shadow: none;
    background-color: #ffffff;
    border: 1px solid #8510df !important;
  }
  [data-slot='input-wrapper'][data-focus-visible='true']:hover,
  [data-slot='input-wrapper']:focus-visible:hover {
    border: 1px solid #8510df !important;
  }
`;

const FieldLabel: React.FC<{
  label: string;
  required: boolean;
  isOpen: boolean;
  isInvalid?: boolean;
}> = ({ label, required = false, isOpen = false, isInvalid = false }) => {
  let labelColorClass = 'text-[#000000]';
  if (isInvalid) {
    labelColorClass = 'text-[#DC2626]';
  } else if (isOpen) {
    labelColorClass = 'text-[#8510DF]';
  }

  return (
    <span
      className={[
        'mb-2 block text-sm font-normal',
        isInvalid
          ? 'text-[#DC2626]'
          : 'text-[#000000] group-focus-within:text-[#8510DF] group-data-[open=true]:text-[#8510DF]',
        labelColorClass,
      ].join(' ')}
    >
      {label}
      {required && (
        <span
          className={
            isInvalid
              ? 'text-[#DC2626]'
              : 'text-[#374151] group-focus-within:text-[#8510DF]'
          }
        >
          &nbsp;*
        </span>
      )}
    </span>
  );
};
FieldLabel.defaultProps = {
  isInvalid: false,
};

const InputShell: React.FC<{
  children: React.ReactNode;
  className: string;
  isInvalid?: boolean;
}> = ({ children, className = '', isInvalid = false }) => (
  <div
    className={[
      'flex h-[42px] tabletMd:h-[36px] desktopMd:h-[42px] items-center rounded-[6px] border text-[#1F2937] bg-white px-3 text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] focus-within:border-[#8510DF]',
      isInvalid
        ? 'border-[#DC2626] focus-within:!border-[#DC2626]'
        : 'border-[#D1D5DB]',
      className,
    ].join(' ')}
  >
    {children}
  </div>
);
InputShell.defaultProps = {
  isInvalid: false,
};

const CurrencySelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onOpenChange: (open: boolean) => void;
}> = ({ value, onChange, onOpenChange }) => (
  <Select
    aria-label="Currency"
    disallowEmptySelection
    selectionMode="single"
    items={currencyOptions}
    selectedKeys={value ? [value] : []}
    onSelectionChange={(keys) => {
      const key = Array.from(keys)[0] as string | undefined;
      if (key) onChange(key);
    }}
    classNames={{
      trigger:
        'min-h-0 h-[42px] tabletMd:h-[36px] desktopMd:h-[42px] w-[80px] rounded-[6px] border border-[#D1D5DB] !bg-white px-3 text-[16px] text-[#1F2937] focus:border-[#8510DF]  data-[open=true]:border-[#8510DF] !ring-0 !ring-offset-0 !outline-0',
      value: 'text-[#1F2937]',
      innerWrapper: 'min-h-0 h-full w-full text-left',
      popoverContent:
        'pt-[12px] pb-[12px] pl-[6px] pr-[6px] bg-white rounded-[16px] shadow-[0_0_25px_0_rgba(0,0,0,0.05)] border border-[rgba(0,0,0,0.05)]',
      selectorIcon:
        'text-[#111827] transition-transform data-[open=true]:rotate-180 static',
      listbox: '[&>ul]:gap-[8px] p-0',
    }}
    onOpenChange={onOpenChange}
  >
    {(item) => (
      <SelectItem
        key={item.key}
        className="text-[#1D1D1D] data-[selected=true]:!text-[#8510DF] data-[selected=true]:!bg-[rgba(208,154,255,0.22)] !bg-white hover:!text-[#8510DF] text-[14px] font-normal  hover:!border-none hover:!ring-0 hover:!ring-offset-0 data-[focus-visible=true]:outline-0"
      >
        {item.label}
      </SelectItem>
    )}
  </Select>
);

const SalaryPeriodSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <Select
    aria-label="Salary cadence"
    disallowEmptySelection
    selectionMode="single"
    items={salaryPeriodOptions}
    selectedKeys={value ? [value] : []}
    onSelectionChange={(keys) => {
      const key = Array.from(keys)[0] as string | undefined;
      if (key) onChange(key);
    }}
    classNames={{
      trigger:
        'min-h-0 h-[42px] tabletMd:h-[36px] desktopMd:h-[42px] w-full sm:w-[128px] rounded-[6px] border border-[#D1D5DB] !bg-white px-3 text-[16px] text-[#1F2937] focus:border-[#8510DF] data-[open=true]:border-[#8510DF] !ring-0 !ring-offset-0 !outline-0',
      value: 'text-[#1F2937]',
      innerWrapper: 'min-h-0 h-full w-full text-left',
      popoverContent:
        'pt-[12px] pb-[12px] pl-[6px] pr-[6px] bg-white rounded-[16px] shadow-[0_0_25px_0_rgba(0,0,0,0.05)] border border-[rgba(0,0,0,0.05)]',
      selectorIcon:
        'text-[#111827] transition-transform data-[open=true]:rotate-180 static',
      listbox: '[&>ul]:gap-[8px] p-0',
    }}
  >
    {(item) => (
      <SelectItem
        key={item.key}
        className="text-[#1D1D1D] data-[selected=true]:!text-[#8510DF] data-[selected=true]:!bg-[rgba(208,154,255,0.22)] !bg-white hover:!text-[#8510DF] text-[14px] font-normal hover:!border-none hover:!ring-0 hover:!ring-offset-0 data-[focus-visible=true]:outline-0"
      >
        {item.label}
      </SelectItem>
    )}
  </Select>
);

export type BasicInformationData = {
  jobTitle: string;
  employmentType: string;
  location: string;
  companyName: string;
  department: string;
  education: string;
  yearsExp: number;
  aboutCompany: Descendant[];
  aboutCompanyText: string;
  minPay: string;
  maxPay: string;
  currency: string;
  salaryPeriod: string;
  travelRequired: string;
  workspaceId?: string;
  workspaceDescription?: Descendant[];
};

const BasicInformation: React.FC<{
  onValidityChange: (valid: boolean) => void;
  onDataChange: (data: BasicInformationData) => void;
  initialData: BasicInformationData | null;
  workspaces: Workspace[];
  validationAttemptCount?: number;
}> = ({
  onValidityChange,
  onDataChange,
  initialData,
  workspaces,
  validationAttemptCount = 0,
}) => {
  const auth = useAuth();
  const { organization } = useOrganization();
  const authToken =
    (auth as { tokens?: { idToken?: { jwtToken?: string } } }).tokens?.idToken
      ?.jwtToken || '';
  const orgId = organization?.orgUuid || '';
  const [jobTitle, setJobTitle] = useState('');
  const [location, setLocation] = useState('');
  const [employmentType, setEmploymentType] = useState('full-time');
  const [isEmploymentOpen, setIsEmploymentOpen] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [department, setDepartment] = useState('');
  const [education, setEducation] = useState('');
  const [yearsExp, setYearsExp] = useState(0);

  const [travelRequired, setTravelRequired] = useState('');
  const [aboutCompany, setAboutCompany] =
    useState<Descendant[]>(createInitialValue());
  const [minPay, setMinPay] = useState('');
  const [maxPay, setMaxPay] = useState('');
  const [salaryPeriod, setSalaryPeriod] = useState(DEFAULT_SALARY_PERIOD);

  const handleAboutCompanyChange = (nextValue: Descendant[]) => {
    setAboutCompany(nextValue);
  };

  const { createWorkspaceMutation } = useCreateWorkspace();

  const [companyOptions, setCompanyOptions] = useState<string[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const didApplyInitialDataRef = useRef(false);
  const companyWorkspaces = useMemo(
    () =>
      workspaces.filter((workspace: Workspace) =>
        isCompanyWorkspace(workspace),
      ),
    [workspaces],
  );

  useEffect(() => {
    if (!initialData || didApplyInitialDataRef.current) {
      return;
    }
    didApplyInitialDataRef.current = true;
    if (!jobTitle && initialData.jobTitle) {
      setJobTitle(initialData.jobTitle);
    }
    if (!employmentType && initialData.employmentType) {
      setEmploymentType(initialData.employmentType);
    }
    if (!location && initialData.location) {
      setLocation(initialData.location);
    }
    if (!department && initialData.department) {
      setDepartment(initialData.department);
    }
    if (!education && initialData.education) {
      setEducation(initialData.education);
    }
    if (initialData.yearsExp !== undefined) {
      setYearsExp(initialData.yearsExp);
    }
    if (!currency && initialData.currency) {
      setCurrency(initialData.currency);
    }
    if (!minPay && initialData.minPay) {
      setMinPay(initialData.minPay);
    }
    if (!maxPay && initialData.maxPay) {
      setMaxPay(initialData.maxPay);
    }
    if (
      salaryPeriod === DEFAULT_SALARY_PERIOD &&
      initialData.salaryPeriod &&
      initialData.salaryPeriod !== DEFAULT_SALARY_PERIOD
    ) {
      setSalaryPeriod(initialData.salaryPeriod);
    }
    if (!travelRequired && initialData.travelRequired) {
      setTravelRequired(initialData.travelRequired);
    }
    if (!selectedCompany && initialData.companyName) {
      setSelectedCompany(initialData.companyName);
    }
    if (!inputValue && initialData.companyName) {
      setInputValue(initialData.companyName);
    }

    const currentAboutText = aboutCompany
      .map((node) => Node.string(node as Descendant))
      .join('')
      .trim();
    if (!currentAboutText && initialData.aboutCompany?.length) {
      setAboutCompany(initialData.aboutCompany);
    }
  }, [
    aboutCompany,
    currency,
    department,
    education,
    employmentType,
    initialData,
    inputValue,
    jobTitle,
    location,
    maxPay,
    minPay,
    salaryPeriod,
    selectedCompany,
    travelRequired,
    yearsExp,
  ]);

  useEffect(() => {
    setCompanyOptions(
      companyWorkspaces
        .map((workspace: Workspace) => workspace.workspaceName)
        .filter((name: string): name is string => Boolean(name)),
    );
  }, [companyWorkspaces]);

  useEffect(() => {
    if (!selectedCompany) return;
    const workspace = companyWorkspaces.find(
      (item: Workspace) =>
        item.workspaceName?.toLowerCase() === selectedCompany.toLowerCase(),
    );
    const description = workspace?.workspaceDescription;
    if (Array.isArray(description) && description.length > 0) {
      setAboutCompany(description);
      return;
    }
    if (typeof description === 'string' && description.trim()) {
      setAboutCompany([
        {
          type: 'paragraph',
          children: [{ text: description.trim() }],
        },
      ]);
    }
  }, [companyWorkspaces, selectedCompany]);

  const matchingItems = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return companyOptions;
    return companyOptions.filter((c) => c.toLowerCase().includes(q));
  }, [inputValue, companyOptions]);

  const rawCompanyInput = inputValue;
  const normalizedCompanyInput = rawCompanyInput.trim();
  const hasExactCompanyMatch = useMemo(() => {
    if (!normalizedCompanyInput) return false;
    return companyOptions.some(
      (company) =>
        company.toLowerCase() === normalizedCompanyInput.toLowerCase(),
    );
  }, [companyOptions, normalizedCompanyInput]);

  const hasRawExactCompanyMatch = useMemo(() => {
    if (!rawCompanyInput) return false;
    return companyOptions.some(
      (company) => company.toLowerCase() === rawCompanyInput.toLowerCase(),
    );
  }, [companyOptions, rawCompanyInput]);

  const shouldShowCreateCompany =
    rawCompanyInput.length > 0 && !hasRawExactCompanyMatch;
  const canCreateCompany =
    shouldShowCreateCompany &&
    !createWorkspaceMutation.isPending &&
    Boolean(authToken && orgId);
  const createCompanyCtaLabel = (() => {
    if (createWorkspaceMutation.isPending) return 'Creating...';
    if (canCreateCompany) return 'Create';
    return 'Keep typing';
  })();

  const handleSelectionChange = (key: React.Key | null) => {
    if (!key) return;
    const value = String(key);
    if (value === CREATE_COMPANY_OPTION_KEY) {
      handleCreateOrSelectCompany();
      return;
    }
    if (value === NO_MATCHING_COMPANIES_OPTION_KEY) {
      return;
    }
    setSelectedCompany(value);
    setInputValue(value);
  };

  const handleCreateOrSelectCompany = () => {
    const nameToAdd = normalizedCompanyInput;
    if (!nameToAdd) return;

    if (hasExactCompanyMatch) {
      setSelectedCompany(nameToAdd);
      setInputValue(nameToAdd);
      return;
    }

    if (!authToken || !orgId || createWorkspaceMutation.isPending) {
      return;
    }

    createWorkspaceMutation.mutate(
      {
        orgId,
        workspaceName: nameToAdd,
        isClientCompany: true,
      },
      {
        onSuccess: () => {
          setCompanyOptions((prev) => {
            const newExists = prev.some(
              (company) => company.toLowerCase() === nameToAdd.toLowerCase(),
            );
            return newExists ? prev : [...prev, nameToAdd];
          });
          setSelectedCompany(nameToAdd);
          setInputValue(nameToAdd);
          setAboutCompany(createInitialValue());
        },
        onError: () => {
          setInputValue('');
          setSelectedCompany(null);
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    handleCreateOrSelectCompany();
  };

  const hasAboutCompanyContent = useMemo(() => {
    const textContent = aboutCompany
      .map((node) => Node.string(node as Descendant))
      .join('')
      .trim();
    return textContent.length > 0;
  }, [aboutCompany]);

  const aboutCompanyText = useMemo(() => {
    return aboutCompany
      .map((node) => Node.string(node as Descendant))
      .join('')
      .trim();
  }, [aboutCompany]);

  const payError = useMemo(() => {
    if (!minPay || !maxPay) return '';
    const min = Number(minPay);
    const max = Number(maxPay);
    if (Number.isNaN(min) || Number.isNaN(max)) return '';
    if (min > max)
      return 'Minimum pay value cannot be greater than the maximum value.';
    return '';
  }, [maxPay, minPay]);

  const expDisplay = `${yearsExp}`;
  const shouldShowValidation = validationAttemptCount > 0;
  const isJobTitleMissing = jobTitle.trim().length === 0;
  const isEmploymentTypeMissing = employmentType.trim().length === 0;
  const isLocationMissing = location.trim().length === 0;
  const isCompanyMissing = (selectedCompany ?? '').trim().length === 0;
  const isEducationMissing = education.trim().length === 0;
  const isAboutCompanyMissing = !hasAboutCompanyContent;

  useEffect(() => {
    const isValid =
      jobTitle.trim().length > 0 &&
      employmentType.trim().length > 0 &&
      location.trim().length > 0 &&
      (selectedCompany ?? '').trim().length > 0 &&
      education.trim().length > 0 &&
      hasAboutCompanyContent &&
      payError.length === 0;
    onValidityChange?.(isValid);
  }, [
    education,
    employmentType,
    hasAboutCompanyContent,
    jobTitle,
    location,
    onValidityChange,
    payError,
    selectedCompany,
  ]);

  useEffect(() => {
    const selectedWorkspace = companyWorkspaces.find(
      (item: Workspace) =>
        item.workspaceName?.toLowerCase() ===
        (selectedCompany ?? '').toLowerCase(),
    );
    onDataChange?.({
      jobTitle,
      employmentType,
      location,
      companyName: selectedCompany ?? '',
      department,
      education,
      yearsExp,
      aboutCompany,
      aboutCompanyText,
      minPay,
      maxPay,
      currency,
      salaryPeriod,
      travelRequired,
      workspaceId: selectedWorkspace?.workspaceId,
      workspaceDescription: aboutCompany,
    });
  }, [
    aboutCompany,
    aboutCompanyText,
    currency,
    department,
    education,
    employmentType,
    jobTitle,
    location,
    maxPay,
    minPay,
    salaryPeriod,
    onDataChange,
    selectedCompany,
    travelRequired,
    yearsExp,
    companyWorkspaces,
  ]);

  return (
    <div className=" !mt-[14px]">
      <div className="space-y-[8px]">
        <h1 className="text-[18px] font-semibold text-[#111827]">
          Basic information
        </h1>
        <p className="text-sm text-[#4B5563]">
          Enter the fundamental details for your job posting
        </p>
      </div>

      <div className="mt-[18px]">
        <div className="grid gap-x-[16px] desktopMd:gap-x-[22px] gap-y-[24px] tabletMd:gap-y-[16px] desktopMd:gap-y-[24px] md:grid-cols-2">
          <div className="group">
            <FieldLabel
              label="Job title"
              required
              isOpen={false}
              isInvalid={shouldShowValidation && isJobTitleMissing}
            />
            <InputShell
              className=""
              isInvalid={shouldShowValidation && isJobTitleMissing}
            >
              <input
                type="text"
                placeholder="e.g. Senior Software Engineer"
                className="h-full w-full bg-transparent text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] placeholder:text-[#ADAEBC] focus:outline-none"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </InputShell>
          </div>
          <div className="group">
            <FieldLabel
              label="Employment type"
              required
              isOpen={isEmploymentOpen}
              isInvalid={shouldShowValidation && isEmploymentTypeMissing}
            />
            <Select
              aria-label="Employment type"
              disallowEmptySelection
              selectionMode="single"
              items={employmentTypeOptions}
              placeholder="Select employment type"
              selectedKeys={employmentType ? [employmentType] : []}
              onOpenChange={setIsEmploymentOpen}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0] as string | undefined;
                if (key) setEmploymentType(key);
              }}
              classNames={{
                trigger: `min-h-0 h-[42px] tabletMd:h-[36px] desktopMd:h-[42px] hover:!bg-white !bg-white w-full rounded-[6px] border shadow-none bg-white px-3 text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] focus:border-[#8510DF] data-[open=true]:border-[#8510DF] data-[focus-visible=true]:outline-0 ${
                  shouldShowValidation && isEmploymentTypeMissing
                    ? '!border-[#DC2626] focus:!border-[#DC2626] data-[open=true]:!border-[#DC2626]'
                    : 'border-[#D1D5DB]'
                }`,
                value: 'text-[#1F2937]',
                innerWrapper: 'h-full w-full text-left',
                popoverContent:
                  'pt-[12px] pb-[12px] pl-[6px] pr-[6px] bg-white rounded-[16px] shadow-[0_0_25px_0_rgba(0,0,0,0.05)] border border-[rgba(0,0,0,0.05)]',
                selectorIcon:
                  'text-[#111827] transition-transform data-[open=true]:rotate-180 static',
                listbox: '[&>ul]:gap-[8px] ',
              }}
            >
              {(item) => (
                <SelectItem
                  key={item.key}
                  className="text-[#1D1D1D] data-[selected=true]:!text-[#8510DF] data-[selected=true]:!bg-[rgba(208,154,255,0.22)] !bg-white hover:!text-[#8510DF] text-[14px] font-normal  hover:!border-none hover:!ring-0 hover:!ring-offset-0 data-[focus-visible=true]:outline-0 "
                >
                  {item.label}
                </SelectItem>
              )}
            </Select>
          </div>
          <div className="group">
            <FieldLabel
              label="Location (city &amp; state)"
              required
              isOpen={false}
              isInvalid={shouldShowValidation && isLocationMissing}
            />
            <InputShell
              className=""
              isInvalid={shouldShowValidation && isLocationMissing}
            >
              <input
                type="text"
                placeholder="e.g. New York, NY"
                className="h-full w-full bg-transparent text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] placeholder:text-[#ADAEBC] focus:outline-none"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </InputShell>
          </div>
          <div className="group">
            <FieldLabel
              label="Company"
              required
              isOpen={false}
              isInvalid={shouldShowValidation && isCompanyMissing}
            />
            <AutocompleteInputWrapper
              isRequired
              isLoading={createWorkspaceMutation.isPending}
              classNames={{
                base: `w-full ${
                  shouldShowValidation && isCompanyMissing
                    ? '[&_[data-slot=input-wrapper]]:!border-[#DC2626]'
                    : ''
                }`,
                popoverContent:
                  'pt-[12px] pb-[12px] pl-[6px] pr-[6px] bg-white rounded-[16px] shadow-[0_0_25px_0_rgba(0,0,0,0.05)] border border-[rgba(0,0,0,0.05)]',
                listbox: '[&>ul]:gap-[8px] ',
                listboxWrapper:
                  'text-[14px] text-[#111827] [&>li]:text-[19px] [&>li]:text-[#111827]',
              }}
              placeholder="Type company name and click enter to add a new company."
              inputValue={inputValue}
              onInputChange={(value) => {
                setInputValue(value);
                if (selectedCompany && value !== selectedCompany) {
                  setSelectedCompany(null);
                }
              }}
              selectedKey={selectedCompany ?? null}
              onSelectionChange={handleSelectionChange}
              onKeyDown={handleKeyDown}
              listboxProps={{
                emptyContent:
                  normalizedCompanyInput.length > 0
                    ? 'No matching companies found.'
                    : 'No companies found.',
              }}
            >
              <>
                {shouldShowCreateCompany ? (
                  <AutocompleteItem
                    key={CREATE_COMPANY_OPTION_KEY}
                    textValue={`Create new company ${normalizedCompanyInput}`}
                    className="!bg-white hover:!border-none hover:!ring-0 hover:!ring-offset-0 data-[focus-visible=true]:outline-0"
                    classNames={{
                      selectedIcon: '!hidden',
                    }}
                  >
                    <div className="flex items-center justify-between gap-3 rounded-[10px] border border-[#E9D5FF] bg-[#FCF8FF] px-3 py-3">
                      <div className="min-w-0">
                        <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[#8B5BC7]">
                          Create new company
                        </div>
                        <div className="mt-1 break-words text-[14px] font-semibold text-[#1F2937]">
                          {normalizedCompanyInput}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 rounded-[7px] border px-2.5 py-1.5 text-[11px] font-semibold ${
                          canCreateCompany
                            ? 'border-[#D8B4FE] bg-white text-[#7B2CBF]'
                            : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#9CA3AF]'
                        }`}
                      >
                        {createCompanyCtaLabel}
                      </span>
                    </div>
                  </AutocompleteItem>
                ) : null}
                {matchingItems.map((item) => (
                  <AutocompleteItem
                    key={item}
                    className="text-[#1D1D1D] data-[selected=true]:!text-[#8510DF] data-[selected=true]:!bg-[rgba(208,154,255,0.22)] !bg-white hover:!text-[#8510DF] text-[14px] font-normal  hover:!border-none hover:!ring-0 hover:!ring-offset-0 data-[focus-visible=true]:outline-0"
                  >
                    {item}
                  </AutocompleteItem>
                ))}
              </>
            </AutocompleteInputWrapper>
          </div>
          <div className="space-y-2 group">
            <FieldLabel
              label="Minimum education"
              required
              isOpen={false}
              isInvalid={shouldShowValidation && isEducationMissing}
            />
            <InputShell
              className=""
              isInvalid={shouldShowValidation && isEducationMissing}
            >
              <input
                type="text"
                placeholder="e.g. Bachelor's degree"
                className="h-full w-full bg-transparent text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] placeholder:text-[#ADAEBC] focus:outline-none"
                value={education}
                onChange={(e) => setEducation(e.target.value)}
              />
            </InputShell>
          </div>
          <div className="space-y-2 group">
            <FieldLabel label="Years of experience" required isOpen={false} />
            <div className="flex items-center gap-2 rounded-[6px] border border-[#D1D5DB] px-[4px] focus-within:border-[#8510DF] bg-white">
              <Button
                isIconOnly
                radius="md"
                variant="bordered"
                className="min-h-0 h-[42px] tabletMd:h-[36px] desktopMd:h-[42px] w-[40px] border-none text-[#111827] !ring-0 !ring-offset-0 !outline-0"
                onPress={() => setYearsExp((prev) => Math.max(0, prev - 1))}
              >
                –
              </Button>
              <div className="flex min-h-0 h-[42px] tabletMd:h-[36px] desktopMd:h-[42px] w-full items-center justify-center text-[14px] text-[#111827]">
                {expDisplay}
              </div>
              <Button
                isIconOnly
                radius="md"
                variant="bordered"
                className="min-h-0 h-[42px] tabletMd:h-[36px] desktopMd:h-[42px] w-[40px] border-none text-[#111827] !ring-0 !ring-offset-0 !outline-0"
                onPress={() => setYearsExp((prev) => prev + 1)}
              >
                +
              </Button>
            </div>
          </div>

          <div className=" group">
            <FieldLabel label="Department" required={false} isOpen={false} />
            <InputShell className="">
              <input
                type="text"
                placeholder="e.g. Engineering"
                className="h-full w-full bg-transparent text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] placeholder:text-[#ADAEBC] focus:outline-none"
                value={department}
                onChange={(e) => {
                  setDepartment(e.target.value);
                }}
              />
            </InputShell>
          </div>
          <div className=" group">
            <FieldLabel
              label="Travel required (%)"
              required={false}
              isOpen={false}
            />
            <InputShell className="">
              <input
                type="number"
                placeholder="10"
                className="h-full w-full bg-transparent text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] placeholder:text-[#ADAEBC] focus:outline-none"
                value={travelRequired}
                onChange={(e) => setTravelRequired(e.target.value)}
              />
            </InputShell>
          </div>

          <div className="col-span-2 grid gap-x-[16px] gap-y-[24px] grid-cols-2 relative">
            <div className="group">
              <FieldLabel
                label="Minimum salary"
                required={false}
                isOpen={isCurrencyOpen}
              />
              <div className="flex flex-col gap-3 sm:flex-row">
                <div>
                  <CurrencySelect
                    value={currency}
                    onChange={setCurrency}
                    onOpenChange={setIsCurrencyOpen}
                  />
                </div>

                <InputShell
                  className={`flex-1 ${payError ? '!border-[#EF4444] focus-within:!border-[#EF4444]' : ''}`}
                >
                  <input
                    type="number"
                    placeholder="50000"
                    className="h-full w-full bg-transparent text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] placeholder:text-[#ADAEBC] focus:outline-none"
                    value={minPay}
                    onChange={(e) => setMinPay(e.target.value)}
                  />
                </InputShell>
              </div>
            </div>
            <div className="group">
              <FieldLabel
                label="Maximum salary"
                required={false}
                isOpen={false}
              />
              <div className="flex flex-col gap-3 sm:flex-row">
                <InputShell
                  className={`flex-1 ${payError ? '!border-[#EF4444] focus-within:!border-[#EF4444]' : ''}`}
                >
                  <input
                    type="number"
                    placeholder="80000"
                    className="h-full w-full bg-transparent text-[16px] tabletMd:text-[14px] desktopSm:text-[16px] text-[#1F2937] placeholder:text-[#ADAEBC] focus:outline-none"
                    value={maxPay}
                    onChange={(e) => setMaxPay(e.target.value)}
                  />
                </InputShell>
                <div>
                  <SalaryPeriodSelect
                    value={salaryPeriod}
                    onChange={setSalaryPeriod}
                  />
                </div>
              </div>
            </div>
            {payError && (
              <p className="text-[10px] text-[#C24D2C] mt-1 absolute bottom-[-20px] left-[92px] w-full">
                {payError}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-[24px] space-y-3">
        <div className="group space-y-2 ">
          <FieldLabel
            label="About the company"
            required
            isOpen={false}
            isInvalid={shouldShowValidation && isAboutCompanyMissing}
          />
          <RichTextEditor
            className={
              shouldShowValidation && isAboutCompanyMissing
                ? 'bg-white !border-[#DC2626] focus-within:!border-[#DC2626]'
                : 'bg-white'
            }
            minHeight="200px"
            maxHeight="200px"
            initialValue={aboutCompany}
            onChange={handleAboutCompanyChange}
            placeholder="Tell candidates about the company culture, mission, and what makes it a great place to work..."
          />
          {shouldShowValidation && isAboutCompanyMissing ? (
            <p className="text-sm text-[#DC2626]">
              Fill in this required field to continue.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
BasicInformation.defaultProps = {
  validationAttemptCount: 0,
};

export default BasicInformation;
